import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { isNil } from "lodash";
import { Grid, LinearProgress, Typography, Divider } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

import { FleetContext, FleetSettingsContext } from "../../context";
import { feeTypes } from "../../constants";
import { KeyValueDisplay } from "../../components/Typography";
import { StringUtils } from "../../util";

const { formatNumber } = StringUtils;

const SettingsBilling = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { activeFleet } = useContext(FleetContext);
  const { activeRatePlan, fetchRatePlan } = useContext(FleetSettingsContext);
  const [loading, setLoading] = useState(false);
  const [minFareEnabled, setMinFareEnabled] = useState(false);

  const handleFetchRatePlan = async () => {
    setLoading(true);
    await fetchRatePlan(activeFleet?.rate_plan_id);
    setLoading(false);
  };

  const checkPaymentMethods = (paymentMethodName) =>
    activeRatePlan?.payment_providers.find(
      (paymentProvider) => paymentProvider.provider === paymentMethodName,
    );

  const getMinFareStatus = () => {
    const status = minFareEnabled ? "Enabled" : "Disabled";
    return t(`Fleet.Settings.Billing.minFare${status}`);
  };

  useEffect(() => {
    if (!activeRatePlan && activeFleet) {
      handleFetchRatePlan(activeFleet.rate_plan_id);
    }
  }, [activeFleet]);

  useEffect(() => {
    if (
      Boolean(activeRatePlan?.min_fleet_charge_amount) &&
      Boolean(activeRatePlan?.min_fleet_charge_minutes)
    ) {
      setMinFareEnabled(true);
    }
  }, [activeRatePlan]);

  return (
    <Grid item container spacing={2} style={{ padding: theme.spacing(1, 2, 2, 2) }}>
      {loading && (
        <LinearProgress color="secondary" data-testid="loading-progress" style={{ padding: 0 }} />
      )}
      <Grid item xs={12}>
        <Typography variant="h5">{`${t("Fleet.Settings.Billing.Rates")}`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Fleet.Settings.Billing.minFare")}
          text={getMinFareStatus()}
          labelMinWidth="30%"
        />
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Fleet.Settings.Billing.currency")}
          text={activeRatePlan?.currency ? activeRatePlan.currency : "None"}
          labelMinWidth="30%"
        />
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Fleet.Settings.Billing.costToUnlock")}
          text={formatNumber(activeRatePlan?.flat_fee, activeRatePlan?.currency)}
          labelMinWidth="30%"
        />
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Fleet.Settings.Billing.costPerMinute")}
          text={formatNumber(activeRatePlan?.time_unit_fee, activeRatePlan?.currency)}
          labelMinWidth="30%"
        />
      </Grid>
      {minFareEnabled && (
        <>
          <Grid item xs={12}>
            <KeyValueDisplay
              label={t("Fleet.Settings.Billing.minFareAmount")}
              text={formatNumber(activeRatePlan?.min_fleet_charge_amount, activeRatePlan?.currency)}
              labelMinWidth="30%"
            />
          </Grid>
          <Grid item xs={12}>
            <KeyValueDisplay
              label={t("Fleet.Settings.Billing.minFareThreshold")}
              text={`${activeRatePlan?.min_fleet_charge_minutes} minutes`}
              labelMinWidth="30%"
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">{t("Fleet.Settings.Billing.Bonuses")}</Typography>
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Fleet.Settings.Billing.signUpBonus")}
          text={formatNumber(activeRatePlan?.first_card_bonus, activeRatePlan?.currency)}
          labelMinWidth="30%"
        />
      </Grid>
      {activeRatePlan?.auto_renew_options?.map((option, i) => (
        <Grid item xs={12} key={option.id}>
          <KeyValueDisplay
            label={i === 0 ? `${t("Fleet.Settings.Billing.walletReplenishment")}` : " "}
            separator={i === 0 ? ":" : ""}
            text={`${formatNumber(option.amount, activeRatePlan?.currency)} amount, ${formatNumber(
              option.bonus,
              activeRatePlan?.currency,
            )} bonus`}
            labelMinWidth="30%"
          />
        </Grid>
      )) || "None"}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">{`${t("Fleet.Settings.Billing.taxesAndFees")}`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={
            activeRatePlan?.tax?.name
              ? t(`${activeRatePlan.tax.name}`)
              : `${t("Fleet.Settings.Billing.tax")}`
          }
          text={
            activeRatePlan?.tax?.type
              ? `${activeRatePlan.tax.rate}%, ${activeRatePlan.tax.type}`
              : `${t("Fleet.Settings.Billing.none")}`
          }
          labelMinWidth="30%"
        />
      </Grid>
      {activeRatePlan?.fees?.map((fee) => (
        <Grid item xs={12} key={fee.id}>
          <KeyValueDisplay
            label={fee?.name}
            text={formatNumber(
              fee?.amount,
              fee.type === feeTypes[0] ? activeRatePlan?.currency : "PERCENT_WHOLE",
            )}
            labelMinWidth="30%"
          />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">{`${t("Fleet.Settings.Billing.fines")}`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography>{`${t("Fleet.Settings.Billing.finesDescription")}`}</Typography>
      </Grid>
      {activeRatePlan?.rate_plan_fine_types.map(({ fine_type_name: fineTypeName, amount }) => (
        <Grid item xs={12} key={fineTypeName}>
          <KeyValueDisplay
            label={`${activeFleet.name} ${fineTypeName}`}
            text={formatNumber(amount, activeRatePlan.currency)}
            labelMinWidth="30%"
          />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">{`${t("Fleet.Settings.Billing.walletAndHolds")}`}</Typography>
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Fleet.Settings.Billing.holdAmount")}
          text={formatNumber(activeRatePlan?.blocking_amount, activeRatePlan?.currency)}
          labelMinWidth="30%"
        />
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Fleet.Settings.Billing.groupHoldAmount")}
          text={
            activeRatePlan &&
            !isNil(activeRatePlan?.blocking_amount) &&
            !isNil(activeRatePlan?.group_rides_hold_adjustment) &&
            !isNil(activeFleet?.max_active_reservations_per_group)
              ? formatNumber(
                  Math.round(
                    activeRatePlan?.blocking_amount *
                      (activeFleet?.max_active_reservations_per_group +
                        Number(activeRatePlan?.group_rides_hold_adjustment)),
                  ),
                  activeRatePlan?.currency,
                )
              : formatNumber(0, activeRatePlan?.currency)
          }
          labelMinWidth="30%"
        />
      </Grid>
      <Grid item xs={12}>
        <KeyValueDisplay
          label={t("Fleet.Settings.Billing.minimumToWaiveHold")}
          tooltip={`${t("Fleet.Settings.Billing.walletToolTipWaiveHold")}`}
          text={formatNumber(activeRatePlan?.min_limit_on_card_no_hold, activeRatePlan?.currency)}
          labelMinWidth="30%"
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h5">{`${t("Fleet.Settings.Billing.PaymentProviders")}`}</Typography>
      </Grid>
      {!loading && (
        <>
          <Grid item xs={12}>
            <KeyValueDisplay
              label={t("Fleet.Settings.Billing.Stripe")}
              text={checkPaymentMethods("stripe") ? "Enabled (Default)" : "Disabled"}
              labelMinWidth="30%"
            />
          </Grid>
          <Grid item xs={12}>
            <KeyValueDisplay
              label={t("Fleet.Settings.Billing.PayPal")}
              text={checkPaymentMethods("braintree") ? "Enabled" : "Disabled"}
              labelMinWidth="30%"
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default SettingsBilling;
